import { Link } from "@tanstack/react-router";
import { Play } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { formatVideoDuration } from "@/lib/video-embed";
import { Img } from "@/components/ui/img";
import { VideoPosterFallback } from "./VideoPosterFallback";
import type { VideoItem } from "@/types";

interface VideoCardProps {
  video: VideoItem;
  /** Where the whole card links; defaults to the public detail page. */
  href?: string;
  onTagClick?: (slug: string) => void;
  /**
   * Nivo naslova kartice: "h2" kad kartice stoje direktno pod h1 stranice
   * (lista /video), "h3" kad su pod h2 sekcijom (povezani videi).
   */
  headingLevel?: "h2" | "h3";
}

export function VideoCard({ video, href, onTagClick, headingLevel = "h3" }: VideoCardProps) {
  const Heading = headingLevel;
  const duration = formatVideoDuration(video.durationSec);
  const to = href ?? `/video/${video.slug}`;

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow hover:shadow-md">
      <Link to={to} className="relative block aspect-video overflow-hidden bg-muted" aria-label={video.title}>
        {video.posterUrl ? (
          <Img
            src={video.posterUrl}
            alt=""
            sizes="(min-width: 1280px) 400px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <VideoPosterFallback slug={video.slug} className="h-full w-full" />
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-white/90 text-primary shadow-lg ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-110">
            <Play weight="fill" className="ml-0.5 size-6" />
          </span>
        </span>
        {duration && (
          <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-medium tabular-nums text-white">
            {duration}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <time className="text-xs text-muted-foreground" dateTime={video.publishedAt ?? video.createdAt}>
          {formatDate(video.publishedAt ?? video.createdAt)}
        </time>
        <Heading
          className="mt-1.5 text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-primary line-clamp-2"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          <Link to={to}>{video.title}</Link>
        </Heading>
        {video.description && (
          <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">{video.description}</p>
        )}
        {video.tags.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {video.tags.map((tag) => (
              <li key={tag.id}>
                {onTagClick ? (
                  <button
                    type="button"
                    onClick={() => onTagClick(tag.slug)}
                    className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/80 transition-colors hover:bg-primary/10 hover:text-primary"
                  >
                    {tag.name}
                  </button>
                ) : (
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/80">{tag.name}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
